import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { ListItemText, MenuItem, Select } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { useEffect } from "react";
import {
  type Control,
  Controller,
  type UseFormSetValue,
} from "react-hook-form";

import { ErrorHelperText } from "../../PatientsDashboard/ui/ErrorHelper";
import { InputLabelText } from "../ui/InputLabelText";
import { InputWrapper } from "../ui/InputWrapper";
import {
  CREDENTIAL_USER_TYPE,
  type CredentialUserType,
  USER_ID_DEFAULT,
  USER_ID_OPTIONS,
} from "./PayerCredentialsForm.zod";

const SELECT_SX = {
  backgroundColor: "#F9FAFB",
  width: "100%",
  borderRadius: "8px",
  ".MuiOutlinedInput-notchedOutline": {
    border: "solid 1px rgba(208, 213, 221, 0.5)",
  },
} as const;

const ROW_SX = {
  width: "100%",
  display: "flex",
  flexDirection: "row" as const,
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
};

interface UserFieldsProps {
  // biome-ignore lint/suspicious/noExplicitAny: shared field component takes any form shape
  control: Control<any>;
  // biome-ignore lint/suspicious/noExplicitAny: shared field component takes any form shape
  setValue: UseFormSetValue<any>;
  initialUserType?: string;
  initialUserId?: string;
  readOnly?: boolean;
}

export function UserFields({
  control,
  setValue,
  initialUserType,
  initialUserId,
  readOnly = false,
}: UserFieldsProps) {
  const resolvedType: CredentialUserType =
    (initialUserType as CredentialUserType) ?? CREDENTIAL_USER_TYPE.AGENT;

  useEffect(() => {
    setValue("userId", initialUserId ?? USER_ID_DEFAULT[resolvedType]);
  }, [resolvedType, setValue, initialUserId]);

  const userIdOptions = USER_ID_OPTIONS[resolvedType];

  return (
    <Controller
      name="userId"
      control={control}
      render={({ field: { name, value, onChange }, fieldState: { error } }) => (
        <FormControl sx={ROW_SX}>
          <FormLabel>
            <InputLabelText>User ID</InputLabelText>
          </FormLabel>
          <InputWrapper>
            <Select
              name={name}
              value={value ?? USER_ID_DEFAULT[resolvedType]}
              onChange={onChange}
              displayEmpty
              disabled={readOnly}
              IconComponent={KeyboardArrowDownOutlinedIcon}
              sx={SELECT_SX}
            >
              {userIdOptions.map((id) => (
                <MenuItem key={id} value={id}>
                  <ListItemText
                    primary={id === "SELF" ? "SELF (current user)" : id}
                  />
                </MenuItem>
              ))}
            </Select>
            <ErrorHelperText message={error?.message} />
          </InputWrapper>
        </FormControl>
      )}
    />
  );
}
